/**
 * Post-auth redirect helper.
 * ---------------------------
 * Persists the path the user came FROM so we can send them back AFTER
 * sign-in / sign-up. Uses localStorage (not sessionStorage) so it
 * survives the email-confirmation round-trip on the signup path:
 *
 *   /interview/invite/XYZ  →  /signup  →  email link  →  /login  →  /interview/invite/XYZ
 *
 * Entries auto-expire after 24h to avoid surprising redirects on a
 * device that hasn't been used for a long time.
 */

const KEY = 'cevee:postAuthRedirect';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Save a target path (relative URL, e.g. "/interview/invite/abc"). */
export function rememberPostAuthRedirect(path: string): void {
  if (!path || !path.startsWith('/')) return;
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({ path, ts: Date.now() }),
    );
  } catch {
    /* private-mode storage failure -- ignore, fall back to ?return= */
  }
}

/**
 * Read + clear the saved redirect path. Returns null if none set or
 * the entry is stale. Always clears the entry to prevent stale
 * redirects on the next visit.
 */
export function consumePostAuthRedirect(): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    localStorage.removeItem(KEY);
    const { path, ts } = JSON.parse(raw) as { path?: string; ts?: number };
    if (!path || !path.startsWith('/')) return null;
    if (!ts || Date.now() - ts > MAX_AGE_MS) return null;
    return path;
  } catch {
    return null;
  }
}
